<template>
  <el-dialog
    title="联系人慰问短信"
    :visible.sync="visible"
    width="780px"
    append-to-body
    :close-on-click-modal="false"
    @closed="handleClosed">
    <el-tabs v-model="activeTab">
      <el-tab-pane label="发送短信" name="send">
        <el-alert
          title="短信将发送到联系人档案中的手机号；实际内容必须与短信平台审核通过的模板保持一致。"
          type="info"
          :closable="false"
          show-icon
          class="mb16"/>
        <el-form label-width="92px">
          <el-form-item label="联系人">
            <span>{{ contact.name || '-' }}</span>
            <span v-if="contact.customerName" class="contact-company">（{{ contact.customerName }}）</span>
          </el-form-item>
          <el-form-item label="手机号">
            <span>{{ displayMobile }}</span>
          </el-form-item>
          <el-form-item label="短信模板" required>
            <el-select v-model="templateId" placeholder="请选择短信模板" style="width: 100%">
              <el-option
                v-for="item in enabledTemplates"
                :key="item.id"
                :label="item.templateName + (isTemplateReady(item) ? '' : '（待配置平台信息）')"
                :value="item.id"/>
            </el-select>
          </el-form-item>
          <el-form-item v-if="selectedTemplate" label="内容预览">
            <div class="sms-preview">{{ selectedTemplate.smsContent }}</div>
            <div class="sms-meta">
              <span>短信签名：{{ selectedTemplate.signName || '未配置' }}</span>
              <span>平台模板编号：{{ selectedTemplate.outerSmsTemplateCode || '未配置' }}</span>
            </div>
          </el-form-item>
        </el-form>
        <el-alert
          v-if="selectedTemplate && !isTemplateReady(selectedTemplate)"
          title="当前模板还没有配置短信平台模板编号或短信签名，请先到“模板管理”中完善。"
          type="warning"
          :closable="false"
          show-icon/>
        <el-empty v-if="!enabledTemplates.length" description="暂无启用的慰问短信模板" :image-size="72"/>
      </el-tab-pane>

      <el-tab-pane label="模板管理" name="manage">
        <div class="template-toolbar">
          <el-button type="primary" icon="el-icon-plus" size="small" @click="handleAddTemplate">新增模板</el-button>
          <span>模板为当前租户共享配置</span>
        </div>
        <el-table :data="templates" v-loading="templateLoading" max-height="390" size="small">
          <el-table-column label="模板名称" prop="templateName" min-width="120"/>
          <el-table-column label="短信内容" prop="smsContent" min-width="240" show-overflow-tooltip/>
          <el-table-column label="平台配置" width="105" align="center">
            <template slot-scope="scope">
              <el-tag v-if="isTemplateReady(scope.row)" type="success" size="mini">已配置</el-tag>
              <el-tag v-else type="warning" size="mini">待配置</el-tag>
            </template>
          </el-table-column>
          <el-table-column label="状态" width="75" align="center">
            <template slot-scope="scope">
              <el-tag :type="scope.row.status === 1 ? 'success' : 'info'" size="mini">
                {{ scope.row.status === 1 ? '启用' : '停用' }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="105" align="center" fixed="right">
            <template slot-scope="scope">
              <el-button type="text" size="small" @click="handleEditTemplate(scope.row)">编辑</el-button>
              <el-button type="text" size="small" class="danger-text" @click="handleRemoveTemplate(scope.row)">删除</el-button>
            </template>
          </el-table-column>
        </el-table>
      </el-tab-pane>
    </el-tabs>

    <span slot="footer" class="dialog-footer">
      <el-button @click="visible = false">关闭</el-button>
      <el-button
        v-if="activeTab === 'send'"
        type="primary"
        :loading="sending"
        :disabled="!selectedTemplate || !isTemplateReady(selectedTemplate)"
        @click="handleSend">确认发送</el-button>
    </span>

    <el-dialog
      :title="templateForm.id ? '编辑短信模板' : '新增短信模板'"
      :visible.sync="templateEditVisible"
      width="640px"
      append-to-body
      :close-on-click-modal="false">
      <el-alert
        title="请先在阿里云短信平台审核短信签名和模板正文，再将审核通过的编号、签名填入这里。"
        type="warning"
        :closable="false"
        show-icon
        class="mb16"/>
      <el-form ref="templateForm" :model="templateForm" :rules="templateRules" label-width="118px">
        <el-form-item label="模板名称" prop="templateName">
          <el-input v-model="templateForm.templateName" maxlength="100" show-word-limit placeholder="例如：日常客户慰问"/>
        </el-form-item>
        <el-form-item label="短信内容" prop="smsContent">
          <el-input
            v-model="templateForm.smsContent"
            type="textarea"
            :rows="6"
            maxlength="500"
            show-word-limit
            placeholder="请输入与短信平台审核内容一致的正文"/>
        </el-form-item>
        <el-form-item label="平台模板编号" prop="outerSmsTemplateCode">
          <el-input v-model="templateForm.outerSmsTemplateCode" maxlength="64" placeholder="例如：SMS_123456789"/>
        </el-form-item>
        <el-form-item label="短信签名" prop="signName">
          <el-input v-model="templateForm.signName" maxlength="64" placeholder="填写短信平台审核通过的签名，不含【】"/>
        </el-form-item>
        <el-form-item label="状态" prop="status">
          <el-radio-group v-model="templateForm.status">
            <el-radio :label="1">启用</el-radio>
            <el-radio :label="2">停用</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="排序" prop="sortNo">
          <el-input-number v-model="templateForm.sortNo" :min="0" :max="9999"/>
        </el-form-item>
        <el-form-item label="备注" prop="remark">
          <el-input v-model="templateForm.remark" maxlength="500" placeholder="选填"/>
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="templateEditVisible = false">取消</el-button>
        <el-button type="primary" :loading="templateSaving" @click="handleSaveTemplate">保存</el-button>
      </span>
    </el-dialog>
  </el-dialog>
</template>

<script>
import {
  listContactCareSmsTemplates,
  removeContactCareSmsTemplate,
  saveContactCareSmsTemplate,
  sendContactCareSms
} from '@/api/crm/contactCareSms'
import { desensitizeMobile } from '@/utils/index'

function emptyTemplateForm() {
  return {
    id: null,
    templateName: '',
    smsContent: '',
    outerSmsTemplateCode: '',
    signName: '',
    status: 1,
    sortNo: 100,
    remark: ''
  }
}

export default {
  name: 'ContactCareSmsDialog',
  data() {
    return {
      visible: false,
      activeTab: 'send',
      contact: {},
      templates: [],
      templateId: null,
      templateLoading: false,
      sending: false,
      templateEditVisible: false,
      templateSaving: false,
      templateForm: emptyTemplateForm(),
      templateRules: {
        templateName: [{ required: true, message: '请输入模板名称', trigger: 'blur' }],
        smsContent: [{ required: true, message: '请输入短信内容', trigger: 'blur' }]
      }
    }
  },
  computed: {
    enabledTemplates() {
      return this.templates.filter(item => Number(item.status) === 1)
    },
    selectedTemplate() {
      return this.templates.find(item => item.id === this.templateId) || null
    },
    displayMobile() {
      if (!this.contact.mobile) {
        return '未维护'
      }
      return this.contact.needHideMobile ? desensitizeMobile(this.contact.mobile) : this.contact.mobile
    }
  },
  methods: {
    open(contact) {
      this.contact = Object.assign({}, contact || {})
      this.activeTab = 'send'
      this.templateId = null
      this.visible = true
      this.loadTemplates()
    },
    loadTemplates(preferredId) {
      this.templateLoading = true
      return listContactCareSmsTemplates().then(response => {
        this.templates = response.data || []
        const preferred = this.templates.find(item => item.id === preferredId && Number(item.status) === 1)
        const current = this.templates.find(item => item.id === this.templateId && Number(item.status) === 1)
        this.templateId = preferred ? preferred.id : (current ? current.id : (this.enabledTemplates[0] ? this.enabledTemplates[0].id : null))
      }).finally(() => {
        this.templateLoading = false
      })
    },
    isTemplateReady(template) {
      return Boolean(template && String(template.outerSmsTemplateCode || '').trim() && String(template.signName || '').trim())
    },
    handleSend() {
      if (!this.contact.id || !this.selectedTemplate || !this.isTemplateReady(this.selectedTemplate)) {
        this.msgWarning('请先选择已完成平台配置的短信模板')
        return
      }
      this.$confirm(`确认使用“${this.selectedTemplate.templateName}”给联系人“${this.contact.name}”发送短信吗？`, '发送确认', {
        confirmButtonText: '确认发送',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.sending = true
        return sendContactCareSms({
          contactId: this.contact.id,
          templateId: this.selectedTemplate.id
        }).then(response => {
          this.msgSuccess(response.data || '短信已提交发送')
          this.visible = false
        }).finally(() => {
          this.sending = false
        })
      }).catch(() => {})
    },
    handleAddTemplate() {
      this.templateForm = emptyTemplateForm()
      this.templateEditVisible = true
      this.$nextTick(() => this.$refs.templateForm && this.$refs.templateForm.clearValidate())
    },
    handleEditTemplate(row) {
      this.templateForm = Object.assign(emptyTemplateForm(), row)
      this.templateEditVisible = true
      this.$nextTick(() => this.$refs.templateForm && this.$refs.templateForm.clearValidate())
    },
    handleSaveTemplate() {
      this.$refs.templateForm.validate(valid => {
        if (!valid) {
          return
        }
        this.templateSaving = true
        saveContactCareSmsTemplate(this.templateForm).then(response => {
          this.msgSuccess('模板保存成功')
          this.templateEditVisible = false
          return this.loadTemplates(response.data)
        }).finally(() => {
          this.templateSaving = false
        })
      })
    },
    handleRemoveTemplate(row) {
      this.$confirm(`确认删除短信模板“${row.templateName}”吗？`, '删除确认', {
        confirmButtonText: '确认删除',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => removeContactCareSmsTemplate(row.id)).then(() => {
        this.msgSuccess('模板已删除')
        return this.loadTemplates()
      }).catch(() => {})
    },
    handleClosed() {
      this.contact = {}
      this.templates = []
      this.templateId = null
      this.templateEditVisible = false
    }
  }
}
</script>

<style lang="scss" scoped>
.mb16 {
  margin-bottom: 16px;
}

.contact-company,
.sms-meta,
.template-toolbar span {
  color: #909399;
}

.sms-preview {
  min-height: 92px;
  padding: 14px 16px;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  background: #f8f9fb;
  color: #303133;
  line-height: 1.8;
  white-space: pre-wrap;
}

.sms-meta {
  display: flex;
  justify-content: space-between;
  margin-top: 6px;
  font-size: 12px;
}

.template-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  font-size: 12px;
}

.danger-text {
  color: #f56c6c;
}
</style>
